import {
    Checkbox
} from '@triumph/design-system';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { CopyToClipboardButton } from '@/app/shared/components/ClipboardCopy/ClipboardCopyButton';
import { InvoicesDashboard, isInvoiceSelectable } from '../dashboard.types';
import NavigateToPageLink from '@/app/shared/components/StyledReactRouterLink/NavigateToPageLink';
import { PageRoutes } from '@/app/components/router/routes';

interface InvoicesTableColumnProps {
    handleNotesChange: (invoiceNumber: string, includeDisputedReasons: boolean) => void
    handlePaymentDateChange: (invoiceNumber: string, selectedPaymentDate: string) => void
    selectedInvoices: Set<string>
    onSelectInvoice: (invoiceNumber: string, isSelected: boolean) => void
    onSelectAllOnPage: (isSelected: boolean) => void
    allCurrentPageSelected: boolean
    someCurrentPageSelected: boolean
    getDisplayValueById?: (id: string | number | null | undefined) => string
}

export const invoicesTableColumns = ({
    selectedInvoices,
    onSelectInvoice,
    onSelectAllOnPage,
    allCurrentPageSelected,
    someCurrentPageSelected,
}: InvoicesTableColumnProps) => {
    const { t } = useTranslation('invoicesDashboard', { keyPrefix: 'table' });

    return [
        {
            order: 0,
            header: '',
            field: 'selection',
            sortable: false,
            renderHeaderLabel: (
                <Checkbox
                    id="select-all-checkbox"
                    checked={allCurrentPageSelected}
                    indeterminate={someCurrentPageSelected}
                    onClick={() => {
                        if (allCurrentPageSelected) {
                            onSelectAllOnPage(false);
                        } else {
                            onSelectAllOnPage(true);
                        }
                    }}
                />
            ),
            dynamicColumnFormatter: (data: any, row: InvoicesDashboard) => {
                const isSelected = selectedInvoices.has(row.invoiceNumber);
                const isDisabled = !isInvoiceSelectable(row);

                return (
                    <Checkbox
                        key={`checkbox-${row.invoiceId}`}
                        id={`checkbox-${row.invoiceId}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        onClick={() => {
                            if (!isDisabled) {
                                onSelectInvoice(row.invoiceNumber, !isSelected);
                            }
                        }}
                    />
                );
            }
        },
        {
            order: 1,
            header: t('invoiceNumber'),
            field: 'invoiceNumber',
            sortable: true,
            dynamicColumnFormatter: (data: any, row: InvoicesDashboard) => {
                return (
                    <div>
                        <NavigateToPageLink
                            pageRoute={PageRoutes.INVOICES.replace(':invoiceId', row.invoiceId.toString())}
                            linkText={row.invoiceNumber}
                            dataTestId={`invoice-link-dashboard-table-`+ row.invoiceId}/>
                    </div>
                )
            }
        },
        {
            order: 2,
            header: t('referenceNumber'),
            field: 'referenceNumber',
            sortable: true,
           dynamicColumnFormatter: (data: any, row: InvoicesDashboard) => {
                const referenceNumber = row.referenceNumber?.trim();
                if (!referenceNumber) {
                    return <div>{t('defaultPlaceholder')}</div>;
                }
                return (
                    <div>
                        <CopyToClipboardButton value={referenceNumber} />
                    </div>
                );
            }
        },
        {
            order: 3,
            header: t('carrierName'),
            field: 'clientName',
            sortable: true,
        },
        {
            order: 4,
            header: t('amount'),
            field: 'amount',
            sortable: true,
        },
        {
            order: 5,
            header: t('invoiceDate'),
            field: 'invoiceDate',
            sortable: true,
        },
        {
            order: 6,
            header: t('invoiceDueDate'),
            field: 'dueDate',
            sortable: true,
        },
        {
            order: 7,
            header: t('age'),
            field: 'age',
            sortable: true,
        },
        {
            order: 8,
            header: t('status'),
            field: 'status',
            sortable: true
        }
    ]
}
